import type { ReploComponent } from "schemas/component";

import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import {
  selectDraftElementId,
  selectDraftElementType,
} from "@editor/reducers/core-reducer";
import { useEditorSelector } from "@editor/store";

import { RuntimeContextProviders } from "replo-runtime/shared/runtime-context";
import { createRuntimeContextStub } from "replo-runtime/shared/runtime-context/create";
import { generateComponentStyleSheetElementContents } from "replo-runtime/shared/styles";
import { ReploComponent as ReploComponentComponent } from "replo-runtime/store/components/ReploComponent";
import { RuntimeContexts } from "replo-runtime/store/contexts/RuntimeContexts";

type IsolatedReploComponentProps = {
  component: ReploComponent;
  variant: "desktop" | "fullSize";
};

const IsolatedReploComponent: React.FC<IsolatedReploComponentProps> = ({
  component,
  variant,
}) => {
  const draftElementId = useEditorSelector(selectDraftElementId);
  const draftElementType = useEditorSelector(selectDraftElementType);
  const projectId = useCurrentProjectId();

  if (!draftElementId || !draftElementType || !projectId) {
    return null;
  }

  const styles = generateComponentStyleSheetElementContents(component, {
    isEditor: false,
    symbols: [],
    featureFlags: {
      carouselDebug: false,
      dataEmDisableOnImages: false,
    },
  });

  const styleAndComponent = (
    <>
      <style>{styles}</style>
      <ReploComponentComponent
        component={component}
        context={{
          selfOrParentHasVariants: false,
          elementId: draftElementId,
          elementType: draftElementType,
          openModalComponent: null,
          state: {},
          attributes: {},
          actionHooks: {},
          isPublishing: false,
          disableLiquid: true,
          useSectionSettings: false,
          store: {},
          globalWindow: window,
        }}
      />
    </>
  );
  return (
    // NOTE (Gabe 2025-04-03): We could hoist RuntimeContexts &
    // RuntimeContextProviders to higher in the component tree, since the canvas
    // & BoundingBoxes are close together, but that means this component would
    // not be usable on it's own (in the sidebar).
    <RuntimeContexts>
      <RuntimeContextProviders
        {...createRuntimeContextStub({
          element: {
            projectId,
            type: draftElementType,
          },
        })}
      >
        {variant === "fullSize" ? (
          styleAndComponent
        ) : (
          <div
            style={{
              width: "216px", // 1200px * 0.15
              height: "144px", // 800px * 0.15
              overflow: "hidden",
              backgroundColor: "#fff",
            }}
          >
            <div
              style={{
                width: "1440px",
                height: "800px",
                transform: "scale(0.15)",
                transformOrigin: "top left",
                overflow: "hidden",
              }}
            >
              {styleAndComponent}
            </div>
          </div>
        )}
      </RuntimeContextProviders>
    </RuntimeContexts>
  );
};

export default React.memo(IsolatedReploComponent);
